import { Activity, ArrowRight, ChevronRight, ExternalLink, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { DispatchMetric, DispatchMetricSeries } from '../../api/types';
import { Note } from '../../components/ErrorNote';
import { useTimeFormat } from '../../lib/tenantTime';
import { isRecordId, type RecordTarget, recordHref } from '../comms/booking/dispatchLink';
import { type Delta, exact } from '../analytics/analyticsFormat';
import { ColumnChart, TrendChart } from './charts/SeriesChart';
import { DeltaChip } from './FaceBody';
import type { ResolvedFrame } from './frames';
import { displayName, metricTypeOf, subcategoryLabel } from './metricMeta';
import { formatMetricRecords } from './metricRecords';
import { type ChartData, SNAPSHOT_PERIOD } from './series';

/**
 * What a figure is made of.
 *
 * The dashboard has two of these (`metric_kpi_detail_page.dart` and
 * `metric_chart_detail_page.dart`); this is one panel that draws either, and it
 * keeps the KPI page's ARRANGEMENT: a summary banner reading previous → change
 * → current, and then the two periods' records SIDE BY SIDE rather than behind
 * a toggle. Side by side is the whole point — the change says a number moved,
 * and the only way to see why is to read both lists at once. A toggle makes
 * that a memory test.
 *
 * Three things below are deliberate:
 *
 * - **The rows are grouped by BUCKET, newest first.** "Which jobs, on which
 *   day" is the question; one flat list of six hundred ids is not an answer.
 * - **A card with several subcategories picks one at a time.** Their record
 *   shapes differ — a quote row and an invoice row are not comparable — and
 *   interleaving them makes a list nobody can scan.
 * - **A truncated list says so.** The figure is never capped, only the rows
 *   carried with it, so a list that simply stopped would read as a card that
 *   disagrees with its own drill-down.
 *
 * On a PHONE (`compact`) the same figures take the whole screen with an X, as
 * every drill-down in the dispatch dashboard's phone app does, and the one
 * rule above that cannot survive the width gives way: two lists side by side
 * need two columns a phone does not have, and stacked, the previous period
 * starts three hundred rows down. So the periods become a toggle — each side
 * carrying its own count, so the comparison is still read at a glance — and
 * the banner's three columns become the figure, its change, and what it is
 * measured against, in one line under it.
 */

/** Past this the panel is a download rather than a read. */
const MAX_ROWS = 300;

interface Group {
  key: string;
  label: string;
  rows: ReturnType<typeof formatMetricRecords>;
}

/** One period's records: everything under a heading that says which period. */
function PeriodPanel({
  title,
  subtitle,
  groups,
  total,
  truncated,
  dashboardOrigin,
  openRecord,
}: {
  title: string;
  subtitle: string;
  groups: Group[];
  total: number;
  truncated: boolean;
  dashboardOrigin: string | null;
  openRecord: ((target: RecordTarget, id: string) => void) | null;
}) {
  // Trimmed across groups rather than within one, so the newest buckets are
  // whole and the oldest are the ones that fall off the end.
  let budget = MAX_ROWS;
  const shown: Group[] = [];
  for (const group of groups) {
    if (budget <= 0) break;
    shown.push({ ...group, rows: group.rows.slice(0, budget) });
    budget -= group.rows.length;
  }

  return (
    <section className="db-period">
      <header>
        <h3>{title}</h3>
        <p>
          {subtitle}
          {total === 0
            ? ''
            : ` · ${
                total > MAX_ROWS || truncated
                  ? `first ${exact(Math.min(total, MAX_ROWS))}${truncated ? '' : ` of ${exact(total)}`}`
                  : `${exact(total)} ${total === 1 ? 'record' : 'records'}`
              }`}
        </p>
      </header>
      {total === 0 ? (
        /* dispatch answers the figure and the rows separately, and older
           buckets can carry one without the other. Said plainly rather than
           drawn as an empty list, which reads like a failure. */
        <Note tone="info">dispatch did not send the rows behind this figure.</Note>
      ) : (
        <div className="db-period-rows">
          {shown.map((group) => (
            <div key={group.key} className="db-detail-group">
              <h4>{group.label}</h4>
              <ul>
                {group.rows.map((row, index) => {
                  const { linkTarget, linkId } = row;
                  // Framed by a host that opens records itself: the row asks
                  // it to, and the agent stays in the app they are in — on the
                  // same route, behind the same permission check, as the row
                  // on its classic dashboard. Otherwise, a link to that route
                  // in a new tab.
                  const open =
                    openRecord && linkTarget && isRecordId(linkId) ? () => openRecord(linkTarget, linkId.trim()) : null;
                  const href = open ? null : recordHref(dashboardOrigin, linkTarget, linkId);
                  const body = (
                    <>
                      <span className="db-detail-row-main">
                        <span className="db-detail-row-title">{row.title}</span>
                        {row.subtitle ? <span className="db-detail-row-sub">{row.subtitle}</span> : null}
                      </span>
                      {row.trailing ? <span className="db-detail-row-figure">{row.trailing}</span> : null}
                      {open ? <ChevronRight size={15} aria-hidden="true" /> : null}
                      {href ? <ExternalLink size={13} aria-hidden="true" /> : null}
                    </>
                  );
                  // No origin, or a row that names nothing addressable: it
                  // still renders and still reads. Nobody is sent to a host
                  // somebody guessed at.
                  return (
                    <li key={`${group.key}:${row.linkId ?? index}`}>
                      {open ? (
                        <button type="button" className="db-detail-row-link" onClick={open}>
                          {body}
                        </button>
                      ) : href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {body}
                        </a>
                      ) : (
                        <span className="db-detail-row-plain">{body}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * One way from a dispatch card to the dispatch board: a bucket the card counts,
 * and how to get there — `open` asks the host (framed), `href` is the board's
 * address in a new tab (standalone). Built by the page, which knows which of
 * the two it has; exactly one of them is set.
 */
export interface BoardLink {
  code: string;
  label: string;
  open: (() => void) | null;
  href: string | null;
}

/**
 * See these jobs on the dispatch board.
 *
 * A dispatch card counts jobs on the board, and the board is where a
 * dispatcher acts on them — so the panel that takes the figure apart offers
 * the way there, opened on the same bucket and the same window the card
 * counted, which is the list those rows came from. One button per bucket: the
 * board filters on one at a time, and which one to look at is the reader's
 * choice.
 */
function BoardLinks({ links, range }: { links: BoardLink[]; range: string }) {
  if (!links.length) return null;
  const single = links.length === 1;
  return (
    <section className="db-board" aria-label="On the dispatch board">
      <span className="db-board-mark" aria-hidden="true">
        <Activity size={16} />
      </span>
      <div className="db-board-text">
        <strong>See these jobs on the dispatch board</strong>
        <span>
          {single
            ? `Opens the board on ${links[0].label}, ${range}.`
            : `Opens the board on one of these, ${range}.`}
        </span>
      </div>
      <div className="db-board-actions">
        {links.map((link) => {
          const label = single ? 'Open the board' : link.label;
          const name = `Open ${link.label} on the dispatch board`;
          return link.open ? (
            <button key={link.code} type="button" className="btn btn-secondary" aria-label={name} onClick={link.open}>
              {label}
              <ArrowRight size={14} aria-hidden="true" />
            </button>
          ) : (
            <a
              key={link.code}
              className="btn btn-secondary"
              aria-label={name}
              href={link.href ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              {label}
              <ExternalLink size={13} aria-hidden="true" />
            </a>
          );
        })}
      </div>
    </section>
  );
}

export function MetricDetail({
  metric,
  rows,
  compareRows,
  compareLabel,
  frame,
  chart,
  highlight,
  widened,
  figure,
  previous,
  delta,
  standing = null,
  truncated,
  dashboardOrigin,
  openRecord = null,
  boardLinks = null,
  compact = false,
  onClose,
}: {
  metric: DispatchMetric;
  /** The series behind this card, already narrowed to it. */
  rows: DispatchMetricSeries[];
  /** The window before this one, where the card draws a change. Null otherwise. */
  compareRows: DispatchMetricSeries[] | null;
  compareLabel: string;
  frame: ResolvedFrame;
  chart: ChartData | null;
  /** The bucket the chart picks out, when it was widened past the window. */
  highlight: string | null;
  widened: boolean;
  /** The headline, pre-formatted by the card so the two can never disagree. */
  figure: string | null;
  previous: string | null;
  delta: Delta | null;
  /**
   * What a figure with nothing to compare against IS, worded as its card words
   * it — all time, or the job board as it stands. The two are not the same
   * claim, and a board count labelled "all time" is a number read wrong.
   */
  standing?: string | null;
  truncated: boolean;
  dashboardOrigin: string | null;
  /** Framed by a host that opens records itself: rows ask it to rather than linking. */
  openRecord?: ((target: RecordTarget, id: string) => void) | null;
  /** A dispatch card's ways to the board it counted, one per bucket. Empty elsewhere. */
  boardLinks?: BoardLink[] | null;
  /** The phone layout: the whole screen, touch charts, and the periods as a toggle. */
  compact?: boolean;
  onClose: () => void;
}) {
  const { fmtDate } = useTimeFormat();
  const codes = metric.subCategory.filter(Boolean);
  const [code, setCode] = useState(() => rows[0]?.subcategory ?? codes[0] ?? '');
  /** Which period's records a phone is showing — the one place the two are not side by side. */
  const [period, setPeriod] = useState<'current' | 'previous'>('current');
  const panel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Full screen is a place of its own: focus moves into it and comes back to
  // the card that opened it, and the page underneath does not scroll along
  // with the list.
  useEffect(() => {
    if (!compact) return;
    const opener = document.activeElement;
    panel.current?.focus({ preventScroll: true });
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      if (opener instanceof HTMLElement) opener.focus({ preventScroll: true });
    };
  }, [compact]);

  const pick = (from: DispatchMetricSeries[] | null) =>
    from?.find((row) => row.subcategory.toUpperCase() === code.toUpperCase()) ?? from?.[0] ?? null;

  const groupsOf = useMemo(
    () =>
      (series: DispatchMetricSeries | null): Group[] => {
        if (!series) return [];
        return [...series.value]
          // Newest first: the question is almost always "what happened lately".
          .sort((a, b) => String(b.period).localeCompare(String(a.period)))
          .map((point) => ({
            key: String(point.period),
            label: point.period === SNAPSHOT_PERIOD ? 'Right now' : fmtDate(String(point.period)),
            rows: formatMetricRecords(
              series.subcategory,
              (Array.isArray(point.data) ? point.data : []) as Record<string, unknown>[],
            ),
          }))
          .filter((group) => group.rows.length > 0);
      },
    [fmtDate],
  );

  const current = groupsOf(pick(rows));
  const before = groupsOf(pick(compareRows));
  const countOf = (groups: Group[]) => groups.reduce((running, group) => running + group.rows.length, 0);

  const title = displayName(metric.metricsName);
  const type = metricTypeOf(metric.type);
  // `range`, not `window`: a local of that name shadows the global one the
  // Escape listener above binds to, and the error lands on the listener.
  const range =
    frame.days === 1
      ? fmtDate(frame.fromIso)
      : `${fmtDate(frame.fromIso)} – ${fmtDate(frame.toIso)}`;
  const beforeRange = `${fmtDate(frame.compare.fromIso)} – ${fmtDate(frame.compare.toIso)}`;
  const comparing = !!delta && !!previous;
  const focusNote = widened ? 'week shown for context' : null;

  if (compact) {
    const twoPeriods = !!compareRows?.length;
    const showingBefore = twoPeriods && period === 'previous';
    const drawn =
      chart && !chart.empty ? (
        type === 'lineChart' ? (
          <TrendChart data={chart} height={200} highlight={highlight} focusNote={focusNote} touch />
        ) : (
          <ColumnChart data={chart} height={200} highlight={highlight} focusNote={focusNote} touch />
        )
      ) : null;

    return (
      <div
        ref={panel}
        className="dbp-detail"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header className="dbp-detail-head">
          <h2>{title}</h2>
          <button type="button" className="dbp-icon-btn" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        <div className="dbp-detail-body">
          <p className="dbp-detail-sub">
            {range}
            {codes.length ? ` · ${codes.map(subcategoryLabel).join(' · ')}` : ''}
          </p>

          {figure || drawn ? (
            <section className="dbp-detail-hero">
              {figure ? (
                <>
                  <div className="dbp-detail-figure">
                    <span>{figure}</span>
                    {delta ? <DeltaChip delta={delta} size={15} /> : null}
                  </div>
                  {/* The banner's three columns, as the one line a phone has
                      room for: what the change is measured against, and what
                      it was there. */}
                  <p className="dbp-detail-when">
                    {comparing ? `vs ${beforeRange} (${previous})` : (standing ?? 'All time, right now')}
                  </p>
                </>
              ) : null}
              {drawn ? <div className="dbp-detail-chart">{drawn}</div> : null}
            </section>
          ) : null}

          <BoardLinks links={boardLinks ?? []} range={range} />

          {rows.length > 1 ? (
            <div className="dbp-chips scroll" role="group" aria-label="Which metric">
              {rows.map((row) => {
                const on = code.toUpperCase() === row.subcategory.toUpperCase();
                return (
                  <button
                    key={row.subcategory}
                    type="button"
                    className={on ? 'dbp-chip on' : 'dbp-chip'}
                    aria-pressed={on}
                    onClick={() => setCode(row.subcategory)}
                  >
                    {subcategoryLabel(row.subcategory)}
                  </button>
                );
              })}
            </div>
          ) : null}

          {twoPeriods ? (
            <div className="dbp-toggle" role="group" aria-label="Which period">
              <button
                type="button"
                className={showingBefore ? '' : 'on'}
                aria-pressed={!showingBefore}
                onClick={() => setPeriod('current')}
              >
                This period <span>{exact(countOf(current))}</span>
              </button>
              <button
                type="button"
                className={showingBefore ? 'on' : ''}
                aria-pressed={showingBefore}
                onClick={() => setPeriod('previous')}
              >
                Previous <span>{exact(countOf(before))}</span>
              </button>
            </div>
          ) : null}

          <PeriodPanel
            title={showingBefore ? beforeRange : range}
            subtitle={twoPeriods ? (showingBefore ? 'Previous period' : 'Current period') : 'Records'}
            groups={showingBefore ? before : current}
            total={countOf(showingBefore ? before : current)}
            truncated={showingBefore ? false : truncated}
            dashboardOrigin={dashboardOrigin}
            openRecord={openRecord}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog db-detail" role="dialog" aria-modal="true" aria-label={title}>
        <header className="db-detail-head">
          <div>
            <h2>{title}</h2>
            <p>
              {range}
              {codes.length ? ` · ${codes.map(subcategoryLabel).join(' · ')}` : ''}
            </p>
          </div>
          <button type="button" className="db-cardbtn" onClick={onClose} aria-label="Close">
            <X size={17} />
          </button>
        </header>

        <div className="db-detail-body">
          {/*
            * The summary banner, in the dashboard's own reading order:
            * previous, then what changed, then current. A single figure with a
            * percentage beside it makes the reader work out which way round it
            * is; three columns say it.
            */}
          {figure ? (
            <div className="db-banner">
              {comparing ? (
                <div className="db-banner-side">
                  <span className="db-banner-label">Previous</span>
                  <span className="db-banner-figure quiet">{previous}</span>
                  <span className="db-banner-when">{beforeRange}</span>
                </div>
              ) : null}
              {delta ? (
                <div className="db-banner-change">
                  <DeltaChip delta={delta} size={14} />
                  <span className="db-banner-when">{compareLabel}</span>
                </div>
              ) : null}
              <div className={comparing ? 'db-banner-side end' : 'db-banner-side'}>
                <span className="db-banner-label">
                  {comparing ? 'Current' : (standing ?? 'All time, right now')}
                </span>
                <span className="db-banner-figure">{figure}</span>
                {comparing ? <span className="db-banner-when">{range}</span> : null}
              </div>
            </div>
          ) : null}

          <BoardLinks links={boardLinks ?? []} range={range} />

          {chart && !chart.empty ? (
            <div className="db-detail-chart">
              {type === 'lineChart' ? (
                <TrendChart data={chart} highlight={highlight} focusNote={focusNote} />
              ) : (
                <ColumnChart data={chart} highlight={highlight} focusNote={focusNote} />
              )}
            </div>
          ) : null}

          {/* Several subcategories are several record SHAPES — a quote row and
              an invoice row are not comparable, so one at a time. It picks for
              BOTH periods, or the two panels would not be comparable either. */}
          {rows.length > 1 ? (
            <div className="db-seg quiet" role="group" aria-label="Which metric">
              {rows.map((row) => (
                <button
                  key={row.subcategory}
                  type="button"
                  className={code.toUpperCase() === row.subcategory.toUpperCase() ? 'on' : ''}
                  onClick={() => setCode(row.subcategory)}
                >
                  {subcategoryLabel(row.subcategory)}
                </button>
              ))}
            </div>
          ) : null}

          {/* Two periods read against each other, which is what the change on
              the card is asking about. One panel where there is no previous. */}
          <div className={compareRows?.length ? 'db-periods two' : 'db-periods'}>
            {compareRows?.length ? (
              <PeriodPanel
                title={beforeRange}
                subtitle="Previous period"
                groups={before}
                total={countOf(before)}
                truncated={false}
                dashboardOrigin={dashboardOrigin}
                openRecord={openRecord}
              />
            ) : null}
            <PeriodPanel
              title={range}
              subtitle={compareRows?.length ? 'Current period' : 'Records'}
              groups={current}
              total={countOf(current)}
              truncated={truncated}
              dashboardOrigin={dashboardOrigin}
              openRecord={openRecord}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
